import { useParams, Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { useState, useCallback, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useProduct } from '@/hooks/useProducts';
import { Button } from '@/components/ui/button';
import { MessageCircle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Product } from '@/hooks/useProducts';

// ── Carrusel manual (flechas + dots) ────────────────────────────────────────

function buildSlides(product: Product): { src: string; label: string }[] {
  const slides: { src: string; label: string }[] = [];
  if (product.image_url) slides.push({ src: product.image_url, label: 'Foto catálogo' });
  if (product.fragrantica_image && product.fragrantica_image !== product.image_url) {
    slides.push({ src: product.fragrantica_image, label: 'Foto oficial' });
  }
  return slides;
}

function ImageCarousel({ product }: { product: Product }) {
  const slides = buildSlides(product);
  const [current, setCurrent] = useState(0);

  const prev = useCallback(() => setCurrent(i => (i - 1 + slides.length) % slides.length), [slides.length]);
  const next = useCallback(() => setCurrent(i => (i + 1) % slides.length), [slides.length]);

  useEffect(() => { setCurrent(0); }, [product.$id]);

  const genderLabel: Record<string, string> = { female: 'Femenino', male: 'Masculino', unisex: 'Unisex' };

  if (slides.length === 0) {
    return (
      <div className="w-full max-w-sm mx-auto h-[420px] bg-muted rounded-xl flex items-center justify-center text-muted-foreground text-sm">
        Sin imagen
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Imagen principal */}
      <div className="relative w-full max-w-sm mx-auto h-[420px] bg-muted rounded-xl overflow-hidden group">
        <img
          key={slides[current].src}
          src={slides[current].src}
          alt={product.name}
          className="w-full h-full object-contain transition-opacity duration-300"
        />

        {/* Badge género */}
        {product.gender && (
          <span className="absolute top-3 left-3 px-3 py-1 text-xs font-body uppercase tracking-widest bg-background/80 text-primary border border-primary/60 rounded-full backdrop-blur-sm">
            {genderLabel[product.gender] ?? product.gender}
          </span>
        )}

        {/* Flechas — solo si hay más de 1 slide */}
        {slides.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Imagen anterior"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              aria-label="Imagen siguiente"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Dots + etiqueta */}
      {slides.length > 1 && (
        <div className="flex flex-col items-center gap-2 mt-3">
          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                aria-label={`Ir a imagen ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              />
            ))}
          </div>
          <span className="font-body text-xs text-muted-foreground tracking-widest uppercase">
            {slides[current].label}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Ficha Fragrantica 2×2 ────────────────────────────────────────────────────

function FragranticaCard({ product }: { product: Product }) {
  const hasData = product.fragrantica_brand || product.fragrantica_year ||
                  product.fragrantica_rating || product.gender;
  if (!hasData) return null;

  const genderLabel: Record<string, string> = { female: 'Femenino', male: 'Masculino', unisex: 'Unisex' };

  const cells = [
    product.fragrantica_brand  && { label: 'Marca',   value: product.fragrantica_brand },
    product.fragrantica_year   && { label: 'Año',     value: product.fragrantica_year },
    product.gender             && { label: 'Género',  value: genderLabel[product.gender] ?? product.gender },
    product.fragrantica_rating && { label: 'Rating',  value: `★ ${product.fragrantica_rating} / 5`, gold: true },
  ].filter(Boolean) as { label: string; value: string; gold?: boolean }[];

  return (
    <div className="mb-6">
      <p className="font-body text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
        Datos Fragrantica
      </p>
      <div className="grid grid-cols-2 gap-2">
        {cells.map(({ label, value, gold }) => (
          <div key={label} className="bg-card border-l-2 border-primary pl-3 pr-2 py-2 rounded-r-md">
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">
              {label}
            </p>
            <p className={`font-body text-sm ${gold ? 'text-yellow-500' : 'text-foreground'}`}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {product.fragrantica_url && (
        <a
          href={product.fragrantica_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 font-body text-xs text-primary/70 hover:text-primary transition-colors tracking-widest uppercase"
        >
          Ver en Fragrantica
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

// ── Comentarios (localStorage, solo lectura) ─────────────────────────────────

function Comments({ productId }: { productId: string }) {
  const [comments] = useState<{ name: string; message: string; createdAt: string }[]>(() => {
    try {
      const raw = localStorage.getItem(`comments:${productId}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  return (
    <div className="mt-8 pt-8 border-t border-border">
      <h3 className="font-display text-lg mb-4">Comentarios</h3>
      <div className="space-y-4">
        {comments.map((c, i) => (
          <div key={i} className="p-4 bg-muted rounded-lg">
            <div className="font-semibold text-sm">
              {c.name}
              <span className="text-xs text-muted-foreground font-normal ml-2">
                · {new Date(c.createdAt).toLocaleString()}
              </span>
            </div>
            <div className="text-sm mt-1 text-muted-foreground">{c.message}</div>
          </div>
        ))}
        {comments.length === 0 && (
          <p className="text-sm text-muted-foreground">No hay comentarios todavía.</p>
        )}
      </div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────

const ProductDetail = () => {
  const { slug } = useParams();
  const { data: product, isLoading, error } = useProduct(slug);

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto p-8 text-muted-foreground">Cargando...</div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto p-8 text-muted-foreground">Producto no encontrado.</div>
        <Footer />
      </div>
    );
  }

  const primaryCategory = product.categories?.[0];
  const whatsappUrl = `https://wa.me/${import.meta.env.VITE_WHATSAPP_PHONE}?text=Hola!%20Me%20interesa%20el%20producto:%20${encodeURIComponent(product.name)}`;
  const description =
    product.fragrantica_description ||
    product.description ||
    primaryCategory?.description ||
    null;

  const seoDescription =
    description ??
    `Descubrí ${product.name} en Tyche. Fragancias y joyería de autor con atención personalizada.`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={product.name}
        description={seoDescription.slice(0, 155)}
        image={product.image_url ?? undefined}
        url={`/producto/${slug}`}
        type="product"
      />
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 lg:px-8">

          {/* Breadcrumb */}
          <nav className="text-sm text-muted-foreground mb-8 font-body" aria-label="Breadcrumb">
            <Link to="/catalogo" className="hover:text-foreground transition-colors">Catálogo</Link>
            <span className="mx-2">/</span>
            {primaryCategory ? (
              <Link
                to={`/catalogo?category=${primaryCategory.slug || primaryCategory.$id}`}
                className="hover:text-foreground transition-colors"
              >
                {primaryCategory.name}
              </Link>
            ) : (
              <span>Categoría</span>
            )}
            <span className="mx-2">/</span>
            <span className="text-foreground">{product.name}</span>
          </nav>

          {/* Grid principal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* Carrusel */}
            <ImageCarousel product={product} />

            {/* Info */}
            <div>
              {/* Marca / título */}
              {product.fragrantica_brand && (
                <p className="font-body text-xs uppercase tracking-[0.3em] text-primary mb-1">
                  {product.fragrantica_brand}
                </p>
              )}
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-2">
                {product.name}
              </h1>
              <div className="line-gold w-10 mb-6" />

              {/* Ficha Fragrantica */}
              <FragranticaCard product={product} />

              {/* Categorías */}
              {product.categories?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Categorías:</span>
                  {product.categories.map(cat => (
                    <Link
                      key={cat.$id}
                      to={`/catalogo?category=${cat.slug || cat.$id}`}
                      className="font-body text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}

              {/* Descripción */}
              {description && (
                <p className="font-body text-sm text-muted-foreground leading-relaxed mb-8">
                  {description}
                </p>
              )}

              {/* CTA */}
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="elegant" size="lg" className="w-full sm:w-auto">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Preguntar por WhatsApp
                </Button>
              </a>

              {/* Comentarios */}
              <Comments productId={product.slug || product.$id} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
